import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp } from "lucide-react";

interface AiAgentMessageInputProps {
	onSend: (message: string) => void;
	disabled?: boolean;
}

export const AiAgentMessageInput = ({
	onSend,
	disabled,
}: AiAgentMessageInputProps) => {
	const { t } = useTranslation();
	const [value, setValue] = useState("");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleSend = useCallback(() => {
		const trimmed = value.trim();
		if (trimmed.length < 3 || trimmed.length > 1000 || disabled) return;
		onSend(trimmed);
		setValue("");
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
		}
	}, [value, disabled, onSend]);

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setValue(e.target.value);
		const el = e.target;
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
	};

	const canSend =
		value.trim().length >= 3 && value.trim().length <= 1000 && !disabled;

	return (
		<div className="ai-chat-input-wrapper">
			<div className="ai-chat-input-container">
				<textarea
					ref={textareaRef}
					value={value}
					onChange={handleInput}
					onKeyDown={handleKeyDown}
					placeholder={t("agent.placeholder")}
					disabled={disabled}
					rows={2}
					className="ai-chat-textarea max-h-[100px]"
				/>
				<button
					onClick={handleSend}
					disabled={!canSend}
					aria-label={t("agent.send")}
					className="ai-chat-send-btn"
				>
					<ArrowUp className="w-3.5 h-3.5" strokeWidth={2.5} />
				</button>
			</div>
		</div>
	);
};
